import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Upload, CheckCircle2, ArrowLeft, FileText, X } from "lucide-react";
import { http } from "../lib/api";
import { BrandLogo } from "../components/BrandLogo";
import WhatsAppFloat from "../components/WhatsAppFloat";
import { getCurrentBrand } from "../hooks/useBranding";

const MAX_FILE_MB = 10;
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp";

// Logo SPM (S sobre M, fondo negro circular)
const SPM_LOGO_URL = "https://customer-assets.emergentagent.com/job_cotiza-ahora/artifacts/sidyu1ey_logo_sin_fondo.png";

export default function RegistroSPM() {
  const [form, setForm] = useState({
    direccion_comunidad: "",
    departamento: "",
    tiene_seguro: "",
    email_contacto: "",
  });
  const [archivo, setArchivo] = useState(null);
  const [website, setWebsite] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`El archivo supera ${MAX_FILE_MB} MB`);
      e.target.value = "";
      return;
    }
    const b64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const result = r.result || "";
        const i = String(result).indexOf(",");
        resolve(i >= 0 ? String(result).slice(i + 1) : String(result));
      };
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    setArchivo({ name: file.name, mime: file.type || "application/octet-stream", base64: b64 });
  };

  const valid = () => {
    if (!form.direccion_comunidad.trim()) return "Ingresa la dirección de la comunidad.";
    if (!form.departamento.trim()) return "Ingresa el número/identificador del departamento.";
    if (!form.tiene_seguro) return "Indica si tienes seguro contratado.";
    if (form.tiene_seguro === "si" && !archivo) return "Adjunta la póliza vigente.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = valid();
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      await http.post("/spm/registro", {
        direccion_comunidad: form.direccion_comunidad.trim(),
        departamento: form.departamento.trim(),
        tiene_seguro: form.tiene_seguro,
        email_contacto: form.email_contacto.trim() || "",
        poliza_archivo_nombre: archivo?.name || null,
        poliza_archivo_base64: archivo?.base64 || null,
        poliza_archivo_mime: archivo?.mime || null,
        website,
        partner: getCurrentBrand()?.key || null,
      });
      setSent(true);
      toast.success("Registro enviado correctamente.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        (typeof detail === "string" && detail) ||
        (err?.message === "Network Error"
          ? "No pudimos conectar. Inténtalo en unos segundos."
          : "No se pudo enviar el registro. Inténtalo nuevamente.");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg-soft)" }}>
        <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center shadow-lg" style={{ border: "1px solid var(--border-medium)" }}>
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-5" style={{ background: "rgba(34,197,94,0.12)" }}>
            <CheckCircle2 size={32} style={{ color: "#15803d" }} />
          </div>
          <h2 className="text-2xl font-extrabold mb-2" style={{ fontFamily: "Manrope", letterSpacing: "-0.01em" }}>
            ¡Registro enviado!
          </h2>
          <p className="text-[14px] mb-6" style={{ color: "var(--text-secondary)" }}>
            Recibimos correctamente la información de tu departamento. Si lo necesitamos, te contactaremos al email indicado.
          </p>
          <Link to="/" className="btn-primary inline-flex" data-testid="spm-back-home">
            <ArrowLeft size={15} /> Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-soft)" }} data-testid="spm-page">
      {/* Mini header propio (sin nav del sitio) */}
      <header className="bg-white border-b" style={{ borderColor: "var(--border-medium)" }}>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="spm-link-home">
            <BrandLogo size={34} />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-[12px] font-semibold transition hover:opacity-70"
            style={{ color: "var(--text-muted)" }}
            data-testid="spm-back-link"
          >
            <ArrowLeft size={13} /> Volver al sitio
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 sm:py-12">
        {/* SPM Logo + Title */}
        <div className="text-center mb-8">
          <img
            src={SPM_LOGO_URL}
            alt="SPM"
            className="h-20 w-20 sm:h-24 sm:w-24 mx-auto mb-5 rounded-full object-contain"
            onError={(e) => { e.target.style.display = "none"; }}
            data-testid="spm-logo"
          />
          <div
            className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-full mb-3"
            style={{ background: "rgba(238,81,71,0.08)", color: "var(--brand-accent)" }}
          >
            <ShieldCheck size={11} /> Obligatorio
          </div>
          <h1
            className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3"
            style={{ fontFamily: "Manrope", letterSpacing: "-0.02em" }}
            data-testid="spm-title"
          >
            Registro seguro obligatorio para departamentos
          </h1>
          <p className="text-[14px] sm:text-[15px] max-w-xl mx-auto" style={{ color: "var(--text-secondary)" }}>
            Complete los datos de su departamento para acreditar el cumplimiento del seguro obligatorio. La información queda registrada de forma confidencial.
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={submit}
          className="bg-white rounded-2xl p-6 sm:p-8 shadow-md"
          style={{ border: "2px solid var(--brand-navy)", boxShadow: "0 8px 32px rgba(10,37,64,0.08)" }}
          data-testid="spm-form"
        >
          {/* Honeypot */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
            aria-hidden="true"
          />

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label required>Dirección de la comunidad</Label>
              <input
                type="text"
                value={form.direccion_comunidad}
                onChange={(e) => set("direccion_comunidad", e.target.value)}
                placeholder="Av. Apoquindo 1234, Las Condes"
                className="field-input field-input--compact"
                required
                data-testid="spm-direccion"
              />
            </div>

            <div>
              <Label required>Departamento</Label>
              <input
                type="text"
                value={form.departamento}
                onChange={(e) => set("departamento", e.target.value)}
                placeholder="Ej: 1205 / Torre B 502"
                className="field-input field-input--compact"
                required
                data-testid="spm-departamento"
              />
            </div>

            <div>
              <Label>Email de contacto <span className="opacity-60 font-normal">(opcional)</span></Label>
              <input
                type="email"
                value={form.email_contacto}
                onChange={(e) => set("email_contacto", e.target.value)}
                placeholder="tucorreo@ejemplo.cl"
                className="field-input field-input--compact"
                data-testid="spm-email"
              />
            </div>

            <div className="sm:col-span-2">
              <Label required>¿Tiene seguro contratado actualmente?</Label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                {[
                  { v: "si", label: "Sí, tengo seguro" },
                  { v: "no", label: "No tengo seguro" },
                ].map((opt) => (
                  <label
                    key={opt.v}
                    className="cursor-pointer rounded-lg border-2 px-4 py-3 transition flex items-center gap-3"
                    style={{
                      borderColor: form.tiene_seguro === opt.v ? "var(--brand-accent)" : "var(--border-medium)",
                      background: form.tiene_seguro === opt.v ? "rgba(238,81,71,0.04)" : "white",
                    }}
                    data-testid={`spm-tiene-seguro-${opt.v}`}
                  >
                    <input
                      type="radio"
                      name="tiene_seguro"
                      value={opt.v}
                      checked={form.tiene_seguro === opt.v}
                      onChange={() => set("tiene_seguro", opt.v)}
                      className="w-4 h-4 accent-current"
                      style={{ color: "var(--brand-accent)" }}
                      required
                    />
                    <span className="text-[14px] font-semibold">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {form.tiene_seguro === "si" && (
              <div className="sm:col-span-2 fade-in-up">
                <Label required>Adjuntar póliza vigente</Label>
                {archivo ? (
                  <div
                    className="flex items-center gap-3 p-3 rounded-lg"
                    style={{ background: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.3)" }}
                  >
                    <FileText size={18} style={{ color: "#15803d" }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                        {archivo.name}
                      </div>
                      <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                        Listo para enviar
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setArchivo(null)}
                      className="p-1.5 rounded hover:bg-white transition"
                      data-testid="spm-remove-file"
                      aria-label="Quitar archivo"
                    >
                      <X size={15} style={{ color: "var(--text-muted)" }} />
                    </button>
                  </div>
                ) : (
                  <label
                    className="flex items-center gap-3 p-4 rounded-lg cursor-pointer transition border-2 border-dashed hover:bg-white"
                    style={{ borderColor: "var(--border-medium)", background: "var(--bg-soft)" }}
                    data-testid="spm-upload-label"
                  >
                    <Upload size={18} style={{ color: "var(--text-muted)" }} />
                    <div className="flex-1">
                      <div className="text-[13px] font-semibold" style={{ color: "var(--text-primary)" }}>
                        Subir archivo (PDF o imagen)
                      </div>
                      <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
                        Máx {MAX_FILE_MB} MB · {ACCEPT}
                      </div>
                    </div>
                    <input
                      type="file"
                      accept={ACCEPT}
                      onChange={onFile}
                      className="hidden"
                      data-testid="spm-poliza-file"
                    />
                  </label>
                )}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full mt-6 justify-center"
            style={{ padding: "0.9rem", fontSize: 15 }}
            data-testid="spm-submit"
          >
            {busy ? "Enviando…" : "Enviar registro"}
          </button>

          <p className="text-[11.5px] text-center mt-4" style={{ color: "var(--text-muted)" }}>
            Sus datos son procesados de forma confidencial y solo serán utilizados para acreditar el cumplimiento del seguro obligatorio.
          </p>
        </form>
      </main>
      <WhatsAppFloat />
    </div>
  );
}

function Label({ children, required }) {
  return (
    <label className="block text-[12px] font-semibold mb-1.5" style={{ color: "var(--text-secondary)" }}>
      {children}
      {required && <span style={{ color: "var(--brand-accent)" }}> *</span>}
    </label>
  );
}
